/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Sparkles, Send, Loader2, Brain, Zap, Database, AlertCircle,
    Lightbulb, RefreshCw, History, Trash2, User, Bot,
} from 'lucide-react';
import api from '../../lib/api';
import AIBadge from '../ai/AIBadge';
import AIThinkingAnimation from '../ai/AIThinkingAnimation';

/**
 * AIAskBox — "Demander à l'IA" avec mémoire conversation persistante
 *
 * - Backend: POST /api/ai/ask, GET /api/ai/ask/conversation, /summary/regenerate
 * - Affiche TOUT l'historique (pas seulement la dernière question)
 * - Bouton "Résumé global" pour récap des conversations passées
 * - Pas de perte de données : chaque échange est sauvegardé
 */
const SUGGESTIONS_BY_ROLE = {
    admin: [
        "Quel est mon agent à recadrer en priorité ce mois et pourquoi ?",
        "Comment relancer mes mandats silencieux depuis +60 jours ?",
        "Quels sont les leviers pour passer 50 % d'exclusivité ?",
    ],
    super_admin: [
        "Quel est mon agent à recadrer en priorité ce mois et pourquoi ?",
        "Comment relancer mes mandats silencieux depuis +60 jours ?",
        "Quels sont les leviers pour passer 50 % d'exclusivité ?",
    ],
    agent: [
        "Comment relancer un vendeur silencieux sans le brusquer ?",
        "Quels arguments pour convertir un mandat simple en exclusif ?",
        "Quelle stratégie pour mes mandats > 90 jours sans offre ?",
    ],
};

/** Light Markdown renderer: **bold**, line breaks, bullets, headings. */
function renderMarkdown(text) {
    if (!text) return null;
    const lines = text.split('\n');
    const blocks = [];
    let listBuf = [];
    const flushList = (key) => {
        if (listBuf.length) {
            blocks.push(
                <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-1.5">
                    {listBuf.map((l, j) => (
                        <li key={`li-${key}-${j}`} className="text-sm text-white/85 leading-relaxed">
                            {inline(l)}
                        </li>
                    ))}
                </ul>,
            );
            listBuf = [];
        }
    };
    const inline = (s) => {
        const parts = s.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((p, idx) => {
            if (p.startsWith('**') && p.endsWith('**')) {
                return (
                    <strong key={idx} className="font-semibold text-white"
                        style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
                        {p.slice(2, -2)}
                    </strong>
                );
            }
            return <span key={idx}>{p}</span>;
        });
    };
    lines.forEach((raw, i) => {
        const line = raw.trimEnd();
        if (/^\s*[-*•]\s+/.test(line)) {
            listBuf.push(line.replace(/^\s*[-*•]\s+/, ''));
            return;
        }
        flushList(i);
        if (!line.trim()) {
            blocks.push(<div key={`sp-${i}`} className="h-1.5" />);
            return;
        }
        if (line.startsWith('### ') || line.startsWith('## ') || line.startsWith('# ')) {
            blocks.push(
                <p key={`h-${i}`} className="text-sm font-semibold text-white mt-2 mb-0.5"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {inline(line.replace(/^#+\s+/, ''))}
                </p>,
            );
            return;
        }
        blocks.push(
            <p key={`p-${i}`} className="text-sm text-white/85 leading-relaxed">
                {inline(line)}
            </p>,
        );
    });
    flushList('end');
    return blocks;
}

function modelBadge(modelUsed) {
    if (!modelUsed) return { label: '', color: '#6B7280', icon: Brain };
    if (modelUsed === 'cached') return { label: 'Cache · 0€', color: '#10B981', icon: Database };
    if (modelUsed.startsWith('claude')) return { label: 'IA Homly', color: '#A855F7', icon: Brain };
    if (modelUsed.startsWith('gemini')) return { label: 'IA Homly', color: '#3B82F6', icon: Zap };
    return { label: modelUsed, color: '#6B7280', icon: Brain };
}

function formatTimestamp(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const today = new Date();
        const sameDay = d.toDateString() === today.toDateString();
        return sameDay
            ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
            : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch (_e) {
        return '';
    }
}

export default function AIAskBox({ role = 'admin', mandateId = null, agentId = null, depth = 'auto' }) {
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [quota, setQuota] = useState({ used: 0, total: 300, remaining: 300 });
    const [messages, setMessages] = useState([]);  // fil de tous les échanges
    const [summary, setSummary] = useState(null);
    const [summaryUpdatedAt, setSummaryUpdatedAt] = useState(null);
    const [showSummary, setShowSummary] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [regeneratingSummary, setRegeneratingSummary] = useState(false);
    const textareaRef = useRef(null);
    const scrollRef = useRef(null);

    const suggestions = SUGGESTIONS_BY_ROLE[role] || SUGGESTIONS_BY_ROLE.admin;
    const persona = role === 'agent' ? 'coach' : 'pilote';

    const loadQuota = useCallback(async () => {
        try {
            const res = await api.get('/ai/ask/quota');
            const d = res?.data || {};
            setQuota({
                used: d.quota_used || 0,
                total: d.quota_total || 300,
                remaining: d.quota_remaining ?? 300,
            });
        } catch (_e) { /* ignore */ }
    }, []);

    const loadHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const res = await api.get('/ai/ask/conversation');
            const d = res?.data || {};
            setMessages(d.messages || []);
            setSummary(d.summary || null);
            setSummaryUpdatedAt(d.summary_updated_at || null);
        } catch (_e) { /* ignore */ } finally {
            setLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        loadQuota();
        loadHistory();
    }, [loadQuota, loadHistory]);

    // Auto-scroll en bas après nouveau message
    useEffect(() => {
        if (scrollRef.current && messages.length > 0) {
            scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
        }
    }, [messages.length, loading]);

    const submit = useCallback(async (textOverride) => {
        const text = (textOverride ?? question).trim();
        if (!text || text.length < 3) return;
        setLoading(true);
        setError(null);
        setQuestion('');

        // Optimistic UI : ajoute la question immédiatement
        const tempId = `tmp-${Date.now()}`;
        const optimistic = {
            id: tempId,
            question: text,
            answer: null,
            model_used: null,
            timestamp: new Date().toISOString(),
            _pending: true,
        };
        setMessages((prev) => [...prev, optimistic]);

        try {
            const res = await api.post('/ai/ask', {
                question: text,
                mandate_id: mandateId || undefined,
                agent_id: agentId || undefined,
                depth,
            });
            const d = res?.data || {};
            // Remplace l'optimistic par la vraie réponse
            setMessages((prev) => prev.map((m) =>
                m.id === tempId ? {
                    id: d.conversation_id || tempId,
                    question: text,
                    answer: d.answer || '',
                    model_used: d.model_used || null,
                    timestamp: new Date().toISOString(),
                } : m,
            ));
            setQuota((q) => ({
                ...q,
                used: d.quota_used ?? q.used,
                total: d.quota_total ?? q.total,
                remaining: Math.max(0, (d.quota_total ?? q.total) - (d.quota_used ?? q.used)),
            }));
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || 'Erreur inconnue';
            setError(detail);
            // Retire l'optimistic en cas d'échec
            setMessages((prev) => prev.filter((m) => m.id !== tempId));
        } finally {
            setLoading(false);
            loadQuota();
        }
    }, [question, mandateId, agentId, depth, loadQuota]);

    const handleRegenerateSummary = useCallback(async () => {
        setRegeneratingSummary(true);
        try {
            const res = await api.post('/ai/ask/summary/regenerate');
            const d = res?.data || {};
            setSummary(d.summary || null);
            setSummaryUpdatedAt(d.updated_at || null);
        } catch (_e) { /* ignore */ } finally {
            setRegeneratingSummary(false);
        }
    }, []);

    const handleClearHistory = useCallback(async () => {
        if (!window.confirm('Vider l\'historique ? Le résumé sera conservé pour garder la trace de ce qui a été dit.')) return;
        try {
            await api.delete('/ai/ask/conversation');
            setMessages([]);
            // Recharge le résumé qui a été préservé/mis à jour avant la suppression
            await loadHistory();
        } catch (_e) { /* ignore */ }
    }, [loadHistory]);

    const onKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            submit();
        }
    };

    const usePct = Math.round((quota.used / Math.max(1, quota.total)) * 100);
    const quotaColor = usePct >= 90 ? '#EF4444' : usePct >= 70 ? '#F59E0B' : '#10B981';
    const hasHistory = messages.length > 0;
    const empty = !hasHistory && !loadingHistory && !loading;

    return (
        <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl"
            style={{ fontFamily: 'IBM Plex Sans, Manrope, sans-serif' }}
            data-testid="ai-ask-box"
        >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 p-5 border-b border-white/5">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] via-[#A855F7] to-[#FF914D] flex items-center justify-center shrink-0">
                        <Brain className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-widest text-white/40"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            Consultant senior IA · mémoire continue
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-white tracking-tight"
                                style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                                Demander à l'IA
                            </h3>
                            <AIBadge persona={persona} size="sm" pulse />
                            {hasHistory && (
                                <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 text-white/50 font-mono"
                                    data-testid="ai-ask-history-count">
                                    {messages.length} échange{messages.length > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0" data-testid="ai-ask-quota-badge">
                    <div className="text-right">
                        <p className="text-[9px] uppercase tracking-widest text-white/40 leading-none"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            Quota agence
                        </p>
                        <p className="text-[13px] font-semibold text-white mt-0.5"
                            style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
                            {quota.used}<span className="text-white/40 font-normal"> / {quota.total}</span>
                        </p>
                    </div>
                    <div className="w-14 h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <motion.div
                            className="h-full rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min(100, usePct)}%` }}
                            transition={{ duration: 0.6 }}
                            style={{ background: quotaColor }}
                        />
                    </div>
                </div>
            </div>

            {/* Actions barre (résumé / clear) */}
            {(hasHistory || summary) && (
                <div className="px-5 py-2.5 border-b border-white/5 flex items-center gap-2 flex-wrap text-[11px]"
                    data-testid="ai-ask-history-toolbar">
                    <button
                        onClick={() => setShowSummary((s) => !s)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 border border-purple-500/30 transition-all"
                        data-testid="ai-ask-toggle-summary"
                    >
                        <History className="w-3 h-3" />
                        {showSummary ? 'Masquer le résumé' : (summary ? 'Voir le résumé global' : 'Pas encore de résumé')}
                    </button>
                    {hasHistory && (
                        <>
                            <button
                                onClick={handleRegenerateSummary}
                                disabled={regeneratingSummary}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10 transition-all disabled:opacity-50"
                                data-testid="ai-ask-regen-summary"
                            >
                                {regeneratingSummary ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                Mettre à jour le résumé
                            </button>
                            <button
                                onClick={handleClearHistory}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 text-white/40 hover:bg-red-500/10 hover:text-red-300 border border-white/10 hover:border-red-500/30 transition-all ml-auto"
                                data-testid="ai-ask-clear"
                            >
                                <Trash2 className="w-3 h-3" />
                                Vider le fil (résumé conservé)
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Résumé global */}
            <AnimatePresence>
                {showSummary && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border-b border-purple-500/20 bg-purple-500/[0.04]"
                        data-testid="ai-ask-summary-panel"
                    >
                        <div className="px-5 py-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                                <p className="text-[11px] uppercase tracking-widest text-purple-300 font-semibold"
                                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                                    Résumé global des conversations
                                </p>
                                {summaryUpdatedAt && (
                                    <span className="text-[10px] text-white/30 ml-auto">
                                        Mis à jour le {new Date(summaryUpdatedAt).toLocaleDateString('fr-FR')}
                                    </span>
                                )}
                            </div>
                            {summary ? (
                                <div className="space-y-1">{renderMarkdown(summary)}</div>
                            ) : (
                                <p className="text-xs text-white/40 italic">
                                    Le résumé sera généré automatiquement après quelques échanges.
                                    Tu peux aussi cliquer sur "Mettre à jour le résumé" pour le forcer.
                                </p>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Fil de conversation */}
            {hasHistory && (
                <div
                    ref={scrollRef}
                    className="px-5 py-4 max-h-[480px] overflow-y-auto space-y-4 border-b border-white/5"
                    data-testid="ai-ask-conversation"
                >
                    {messages.map((m, idx) => (
                        <MessageBubble key={m.id || idx} message={m} />
                    ))}
                </div>
            )}

            {/* Composer */}
            <div className="p-5 space-y-3">
                <div className="relative">
                    <textarea
                        ref={textareaRef}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={onKeyDown}
                        rows={hasHistory ? 2 : 3}
                        maxLength={500}
                        disabled={loading || quota.remaining === 0}
                        placeholder={hasHistory
                            ? "Pose une nouvelle question, je me souviens de ce qu'on a dit..."
                            : "Ex : Quel est mon agent à recadrer ce mois et pourquoi ? Donne-moi une stratégie sur 2 semaines."}
                        className="w-full resize-none rounded-xl bg-black/30 border border-white/10 focus:border-[#A855F7]/50 focus:ring-1 focus:ring-[#A855F7]/30 outline-none px-4 py-3 text-sm text-white placeholder-white/30 transition-all disabled:opacity-50"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        data-testid="ai-ask-input"
                    />
                    <div className="absolute bottom-2 right-2 text-[10px] text-white/30"
                        style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
                        {question.length}/500
                    </div>
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5 text-[11px] text-white/40"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        <Lightbulb className="w-3 h-3 text-[#FF914D]" />
                        <span>Mémoire continue : l'IA se souvient de vos échanges pour des conseils en contexte</span>
                    </div>
                    <button
                        onClick={() => submit()}
                        disabled={loading || question.trim().length < 3 || quota.remaining === 0}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#681793] to-[#A855F7] hover:from-[#7d1bae] hover:to-[#b665fb] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold shadow-lg shadow-[#A855F7]/20 transition-all"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        data-testid="ai-ask-submit"
                    >
                        {loading ? (
                            <><Loader2 className="w-4 h-4 animate-spin" />Analyse…</>
                        ) : (
                            <><Send className="w-3.5 h-3.5" />Demander</>
                        )}
                    </button>
                </div>

                {/* Loading thinking animation (uniquement si pas optimistic UI déjà visible) */}

                {/* Suggestions (état vide) */}
                {empty && (
                    <div className="pt-1">
                        <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            Suggestions
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {suggestions.map((s, i) => (
                                <button
                                    key={`sug-${i}`}
                                    onClick={() => submit(s)}
                                    className="text-left text-xs text-white/70 hover:text-white px-3 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 transition-all"
                                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                                    data-testid={`ai-ask-suggestion-${i}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Erreur */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5 flex items-start gap-3"
                            data-testid="ai-ask-error"
                        >
                            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm text-amber-200"
                                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                                    {error}
                                </p>
                            </div>
                            <button
                                onClick={() => setError(null)}
                                className="text-amber-300 hover:text-amber-100 transition-colors"
                                data-testid="ai-ask-retry"
                            >
                                <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Footer signature */}
            <div className="px-5 py-3 border-t border-white/5 flex items-center gap-2 text-[11px] text-white/40">
                <Sparkles className="w-3 h-3 text-[#FF914D]" />
                <span style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    IA Homly · {quota.total} questions/mois · mémoire conservée à vie
                </span>
            </div>
        </motion.section>
    );
}

function MessageBubble({ message }) {
    const isPending = message._pending;
    const badge = modelBadge(message.model_used);
    const Icon = badge.icon;
    return (
        <div className="space-y-2" data-testid="ai-ask-message">
            {/* User question */}
            <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <User className="w-3.5 h-3.5 text-white/60" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-[11px] font-semibold text-white/70"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            Vous
                        </span>
                        <span className="text-[10px] text-white/30">{formatTimestamp(message.timestamp)}</span>
                    </div>
                    <p className="text-sm text-white/90 leading-relaxed">{message.question}</p>
                </div>
            </div>

            {/* AI answer */}
            <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                    <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[11px] font-semibold text-white/70"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            Homly IA
                        </span>
                        {!isPending && badge.label && (
                            <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider border"
                                style={{
                                    background: `${badge.color}1f`,
                                    color: badge.color,
                                    borderColor: `${badge.color}55`,
                                    fontFamily: 'Space Grotesk, sans-serif',
                                }}
                            >
                                <Icon className="w-2.5 h-2.5" />
                                {badge.label}
                            </span>
                        )}
                    </div>
                    {isPending ? (
                        <div className="py-1">
                            <AIThinkingAnimation />
                        </div>
                    ) : (
                        <div className="rounded-xl bg-black/20 border border-white/5 p-3 space-y-1">
                            {renderMarkdown(message.answer)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
